import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { QueryParameters, UTMParameters } from "@lifeat/shared/system/RouteConstants";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { ModalNotification } from "@common/LifeAtModal/LifeAtModal";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import CopyLinkIcon from "@icons/CopyTextIcon.svg";
import EmailIcon from "@icons/EmailIcon.svg";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { addQueryParams } from "@util/RouteUtil";
import { BrowserUtil, InteractUtil, RouteUtil } from "../../../common/util";
import { RoomModal, RoomSettingLabelPrimary, RoomSettingLabelSecondary } from "./RoomModalCommonElements";

const RoomSettingContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    width: 100%;
`;

const RoomSettingRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const RoomSettingInfo = styled.span`
    color: ${(props) => props.theme.textColorSpecial};
    font-size: ${FontSize.small};
    margin: 4px 0;
`;

const LinkInputWrapper = styled.span`
    width: 80%;

    cursor: pointer;
    align-self: center;
    margin-right: 5px;
`;

const LinkInput = styled.input`
    min-height: 36px;
    height: auto;
    width: 100%;

    margin: 4px 0;
    padding: 6px 12px;

    border: none;
    border-radius: ${Styles.borderRadiusSharp};
    background-color: #e3e3e3;
    cursor: pointer;

    font-size: ${FontSize.normal};
`;

const LinkButtonsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    width: auto;
    gap: 5px;
`;

const ShareActionButton = styled(LifeAtButton)`
    justify-content: center;
    margin: 4px 0;

    .lifeat-button-icon {
        display: flex;
    }
`;

const StyledCopyLinkIcon = styled(CopyLinkIcon)`
    fill: ${(props) => props.theme.textColorPrimary};
`;

const StyledEmailIcon = styled(EmailIcon)`
    fill: ${(props) => props.theme.textColorPrimary};
`;

const INVITE_BTN_LABEL = "Invite";
const MODAL_TITLE = "Invite Friends";

const LINK_COPIED_NOTIFICATION = "Link copied to clipboard";
const COPY_LINK_NOTIF_TIMEOUT_MS = 5000;

const VID_CHAT_UPGRADE_CTA_LABEL = "🌟 Upgrade for more than 15 minutes of video calling per day!";
const ROOM_LINK_LABEL = "Your room link";
const ROOM_SETTINGS_CTA = "Room settings have moved! Click on your Room name to access them.";

interface RoomInviteModalProps {
    readonly roomId: string;
    readonly roomName: string;
}
export default function RoomInviteModal(props: RoomInviteModalProps) {
    const { roomId, roomName } = { ...props };

    const history = useHistory();

    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const { userData }: UserContextType = useUserContext();

    const roomOwnedByUser = isRoomOwnedByUser(roomId, userData);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalNotifications, setModalNotifications] = useState<ModalNotification[]>([]);

    const [shareableRoomLink, setShareableRoomLink] = useState<string>(window.location.href);
    useEffect(() => {
        if (roomId && roomName) {
            const roomQueryParams = RouteUtil.updateQueryParams(QueryParameters.RoomHost, roomName);
            history.replace({ search: roomQueryParams });

            let link = `${window.location.origin}${window.location.pathname}?${roomQueryParams}`;
            link = addQueryParams(link, {
                utm_source: UTMParameters.roomInvite.utm_source,
                utm_medium: UTMParameters.roomInvite.utm_medium,
            });
            if (link.includes("enter=true")) {
                link = `${window.location.origin}${window.location.pathname}?${RouteUtil.updateQueryParams(
                    QueryParameters.EnterRoom,
                    null,
                )}`;
            }
            setShareableRoomLink(link);
        }
    }, [roomId, roomName]);

    const handleInviteBtnClick = () => {
        setModalNotifications([]);
        setIsModalOpen(true);
    };

    // The button that opens the modal.
    const inviteButton = (
        <LifeAtButtonV2
            onClick={handleInviteBtnClick}
            overrideTextColor="white"
            overrideBackgroundColor={Styles.buttonFillBlue}
            disableHover
            size={"small"}
        >
            {INVITE_BTN_LABEL}
        </LifeAtButtonV2>
    );

    const handleCopyLinkButtonClick = () => {
        InteractUtil.copyToClipboard(shareableRoomLink);
        setModalNotifications([
            {
                type: "info",
                message: LINK_COPIED_NOTIFICATION,
            },
        ]);
        setTimeout(() => setModalNotifications([]), COPY_LINK_NOTIF_TIMEOUT_MS);
    };

    const copyRoomLinkButton = (
        <ShareActionButton isLink={false} icon={<StyledCopyLinkIcon />} handleClick={handleCopyLinkButtonClick} />
    );

    const handleInviteViaEmail = () => {
        const link = `mailto:?&subject=Join%20my%20room%20in%20LifeAt%20%f0%9f%91%8b&cc=&bcc=&body=${shareableRoomLink}`;
        BrowserUtil.openLinkNewTab(link);
    };

    const shareRoomLinkViaEmailButton = (
        <ShareActionButton isLink={false} icon={<StyledEmailIcon />} handleClick={handleInviteViaEmail} />
    );

    const roomSocialEls = (
        <RoomSettingContainer key={`room-social-modal-link-elements`} className={`room-setting-row room-social-els`}>
            <RoomSettingLabelSecondary>{ROOM_LINK_LABEL}</RoomSettingLabelSecondary>
            <RoomSettingRow>
                <LinkInputWrapper onClick={handleCopyLinkButtonClick}>
                    <LinkInput
                        key={`room-social-modal-url-input-field`}
                        className={`room-link-input`}
                        value={shareableRoomLink}
                        disabled={true}
                    />
                </LinkInputWrapper>

                <LinkButtonsWrapper>
                    {copyRoomLinkButton}
                    {shareRoomLinkViaEmailButton}
                </LinkButtonsWrapper>
            </RoomSettingRow>
        </RoomSettingContainer>
    );

    const modalRowElements = (
        <>
            {!userData?.proUserInfo?.isProUser && roomOwnedByUser ? (
                <RoomSettingLabelPrimary>
                    <LinkButton
                        label={VID_CHAT_UPGRADE_CTA_LABEL}
                        handleClick={() => setUpgradeCtaContext("unlimitedVideoChat", UpgradeCTASource.ROOMS)}
                    />
                </RoomSettingLabelPrimary>
            ) : (
                <></>
            )}
            {roomSocialEls}
            {roomOwnedByUser ? <RoomSettingInfo>{ROOM_SETTINGS_CTA}</RoomSettingInfo> : <></>}
        </>
    );

    const closeModalAction = () => {
        setModalNotifications([]);
        setIsModalOpen(false);
    };

    return (
        <>
            {inviteButton}

            <RoomModal
                key={`room-invite-modal`}
                title={MODAL_TITLE}
                isOpen={isModalOpen}
                closeModal={closeModalAction}
                notifications={modalNotifications}
            >
                {modalRowElements}
            </RoomModal>
        </>
    );
}
